/**
 * Permission denial monitor
 *
 * Installs a global `fetch` interceptor that inspects PostgREST responses from
 * the project's Supabase URL. When a `42501` / "permission denied" error is
 * observed, the details are reported to the `report-permission-denial` edge
 * function which records them in `security_alerts`.
 *
 * Purpose: catch authorization regressions from the anon-EXECUTE lockdown
 * (Batches 1-3) or any future RLS/grant change the moment a real user hits
 * one, instead of waiting for a manual log audit.
 *
 * Safe by design:
 *  - Client-side dedupe (60s per fingerprint) via sessionStorage.
 *  - Silent failures — never throws into the app's normal fetch path.
 *  - Only fires for supabase.co REST/RPC responses.
 *  - No PII in fingerprint (hash of code + path + message prefix).
 */
const SUPABASE_HOST = 'ewdidxzgywdsvygvdcji.supabase.co';
const REPORT_ENDPOINT = `https://${SUPABASE_HOST}/functions/v1/report-permission-denial`;
const DEDUPE_WINDOW_MS = 60_000;
const DEDUPE_KEY_PREFIX = '__pd_seen__:';

let installed = false;

function shortHash(input: string): string {
  // Non-cryptographic 32-bit hash; sufficient for client-side dedupe fingerprints.
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function recentlyReported(fingerprint: string): boolean {
  try {
    const key = DEDUPE_KEY_PREFIX + fingerprint;
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (Number.isFinite(at) && Date.now() - at < DEDUPE_WINDOW_MS) return true;
  } catch {
    // sessionStorage may be unavailable (SSR, private mode) — proceed without dedupe.
  }
  return false;
}

function markReported(fingerprint: string): void {
  try {
    sessionStorage.setItem(DEDUPE_KEY_PREFIX + fingerprint, String(Date.now()));
  } catch {
    /* ignore */
  }
}

function isPermissionDenied(payload: unknown): { code: string; message: string } | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const code = typeof p.code === 'string' ? p.code : '';
  const message = typeof p.message === 'string' ? p.message : '';
  const hint = typeof p.hint === 'string' ? p.hint : '';
  if (code === '42501' || /permission denied/i.test(message) || /permission denied/i.test(hint)) {
    return { code: code || '42501', message: message || hint || 'permission denied' };
  }
  return null;
}

async function report(details: {
  code: string;
  message: string;
  path: string;
  method: string;
}): Promise<void> {
  const fingerprint = shortHash(`${details.code}|${details.method}|${details.path}|${details.message.slice(0, 120)}`);
  if (recentlyReported(fingerprint)) return;
  markReported(fingerprint);

  try {
    // Best-effort; do not await failure. keepalive lets it complete during navigation.
    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        code: details.code,
        message: details.message,
        path: details.path,
        method: details.method,
        fingerprint,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      }),
    });
  } catch {
    // Swallow — monitor must never break the app.
  }
}

export function installPermissionDenialMonitor(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await original(input, init);
    try {
      const url =
        typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      if (!url.includes(SUPABASE_HOST)) return response;
      // Only inspect REST / RPC paths where 42501 would surface as a JSON body.
      if (!/\/rest\/v1\//.test(url)) return response;
      if (response.ok) return response;
      const clone = response.clone();
      const contentType = clone.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return response;
      const body = await clone.json().catch(() => null);
      const hit = isPermissionDenied(body);
      if (hit) {
        const method = (init?.method || 'GET').toUpperCase();
        // Strip query string from path to keep fingerprints stable.
        const path = url.split('?')[0].split(SUPABASE_HOST)[1] || url;
        void report({ code: hit.code, message: hit.message, path, method });
      }
    } catch {
      // Never let monitoring throw into app code.
    }
    return response;
  };
}
